import { Session } from '@auth/domain/models/Session'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  LocalStorage,
  localStorageDatasource,
  localStorageRepositoryAdapter
} from '@/modules/localStorage'
import { app } from '@/ui/config/firebase'

import { authRouteNames } from '../routes/routesNames/authRouteNames'

type PersonaSession = Session['personaAuthenticationResponses'][number]

const localStorage = LocalStorage(
  localStorageRepositoryAdapter(localStorageDatasource)
)

const authContext = createContext<{
  userSession: Session | null
  setUserSession: (session: Session) => void
  personaSession: PersonaSession | null
  setPersonaSession: (personaSession: PersonaSession) => void
  isFirebaseAuthenticated: boolean
  setIsFirebaseAuthenticated: (isFirebaseAuthenticated: boolean) => void
}>({
  userSession: null,
  setUserSession: () => ({}),
  personaSession: null,
  setPersonaSession: () => ({}),
  isFirebaseAuthenticated: false,
  setIsFirebaseAuthenticated: () => ({})
})

export const useAuthContext = () => useContext(authContext)

const publicRoutes: string[] = [
  authRouteNames.home,
  authRouteNames.login,
  authRouteNames.signup,
  authRouteNames.forgotPassword,
  authRouteNames.changePassword,
  authRouteNames.changePasswordSuccessful,
  authRouteNames.insertPin,
  authRouteNames.selectRecover,
  authRouteNames.forgotLogin,
  authRouteNames.recoverLoginSuccesFul,
  authRouteNames.insertPinLogin
]

export default function AuthProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [userSession, setUserSession] = useState<Session | null | undefined>()
  const [personaSession, setPersonaSession] = useState<
    PersonaSession | null | undefined
  >()
  const [isFirebaseAuthenticated, setIsFirebaseAuthenticated] =
    useState<boolean>(false)

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    try {
      setUserSession(localStorage.getUser())
    } catch {
      setUserSession(null)
    }

    try {
      setPersonaSession(localStorage.getPersona())
    } catch {
      setPersonaSession(null)
    }
  }, [])

  useEffect(() => {
    try {
      const auth = getAuth(app)
      const token = personaSession?.firebaseToken ?? ''

      if (personaSession) {
        signInWithCustomToken(auth, token).then((_) =>
          setIsFirebaseAuthenticated(true)
        )
      }
    } catch (err) {
      console.error(err)
    }
  }, [personaSession])

  useEffect(() => {
    if (location.pathname === authRouteNames.login)
      navigate(authRouteNames.home, { replace: true })

    if (userSession === undefined) return

    if (!userSession) {
      if (!publicRoutes.includes(location.pathname)) navigate('/login')
      return
    }

    if (personaSession != null) {
      window.moviDeskAuth(personaSession)
    }

    if (publicRoutes.includes(location.pathname)) {
      const redirectSearchParam = location.search.match(/redirect=(\w|\/|-|_)+/)
      const redirect =
        redirectSearchParam && redirectSearchParam[0].split('=')[1]
      navigate(redirect ?? authRouteNames.access, { replace: true })
    }
  }, [location, userSession])

  return (
    <authContext.Provider
      value={{
        userSession: userSession ?? null,
        setUserSession,
        personaSession: personaSession ?? null,
        setPersonaSession,
        isFirebaseAuthenticated: isFirebaseAuthenticated,
        setIsFirebaseAuthenticated
      }}
    >
      {children}
    </authContext.Provider>
  )
}
